import { Controller } from "@hotwired/stimulus"

// Drives a per-endpoint "Test Request" widget on the API docs page.
//
// Inputs (data-test-request-target):
//   baseUrl, token, companyId
//   bodyParam (one per request-body field, named via data-param-name; coerced
//     by data-param-type, blank values omitted, assembled into a JSON body)
//   pathParam (one per path placeholder, named via data-param-name)
//   queryParam (one per query parameter, named via data-param-name)
//
// On submit:
//   1. Substitutes path placeholders ({id}, ...) with their input values.
//   2. Appends non-blank query params.
//   3. Sends fetch with Authorization: Bearer <token> and Company-Id when set.
//   4. Renders status, time, headers (truncated), and pretty-printed JSON body.
//
// Cross-origin / CORS is the user's responsibility. The default Base URL
// points at the same host as the docs page; changing it may require CORS.
//
// Persists the Base URL, Token, and Company-Id in localStorage to reduce
// re-typing across reloads. Per-endpoint params are not persisted (too noisy).
const STORAGE_KEY = "ccs-api-docs-test-request"

export default class extends Controller {
  static targets = [
    "baseUrl",
    "bodyParam",
    "companyId",
    "copyButton",
    "curl",
    "form",
    "pathParam",
    "queryParam",
    "responseBody",
    "responseHeaders",
    "responseStatus",
    "responseWrapper",
    "token",
  ]

  static values = {
    bodyTemplate: String,
    method: String,
    path: String,
    requiresCompanyId: Boolean,
  }

  // Assembles the request-body fields into a JSON string. Blank fields are
  // omitted; values are coerced per data-param-type. Returns null when there
  // are no body fields (so no Content-Type/body is sent).
  buildBody() {
    if (!this.hasBodyParamTarget) return null

    const obj = {}
    this.bodyParamTargets.forEach((el) => {
      if (el.value === "") return
      const name = el.dataset.paramName
      switch (el.dataset.paramType) {
        case "boolean":
          obj[name] = el.value === "true"
          break
        case "integer":
        case "number": {
          const n = Number(el.value)
          if (!Number.isNaN(n)) obj[name] = n
          break
        }
        case "array":
          try {
            const parsed = JSON.parse(el.value)
            obj[name] = Array.isArray(parsed) ? parsed : [parsed]
          } catch (_e) {
            obj[name] = el.value.split(",").map((s) => s.trim()).filter(Boolean)
          }
          break
        case "object":
          try {
            obj[name] = JSON.parse(el.value)
          } catch (_e) {
            obj[name] = el.value
          }
          break
        default:
          obj[name] = el.value
      }
    })

    return JSON.stringify(obj)
  }

  buildRequest() {
    const base = (this.baseUrlTarget.value || window.location.origin).replace(/\/$/, "")
    const url = `${base}${this.requestPath()}${this.queryString()}`

    const headers = {}
    if (this.hasTokenTarget && this.tokenTarget.value) {
      headers["Authorization"] = `Bearer ${this.tokenTarget.value}`
    }
    if (this.requiresCompanyIdValue && this.hasCompanyIdTarget && this.companyIdTarget.value) {
      headers["Company-Id"] = this.companyIdTarget.value
    }

    const options = { method: this.methodValue.toUpperCase(), headers }
    const body = this.buildBody()
    if (body !== null && options.method !== "GET" && options.method !== "HEAD") {
      headers["Content-Type"] = "application/json"
      options.body = body
    }

    return { url, options }
  }

  connect() {
    const saved = this.readSaved()
    if (saved.baseUrl && this.hasBaseUrlTarget && !this.baseUrlTarget.value)
      this.baseUrlTarget.value = saved.baseUrl
    if (saved.token && this.hasTokenTarget && !this.tokenTarget.value)
      this.tokenTarget.value = saved.token
    if (saved.companyId && this.hasCompanyIdTarget && !this.companyIdTarget.value)
      this.companyIdTarget.value = saved.companyId
    this.refreshCurl()
  }

  persistShared() {
    const data = this.readSaved()
    if (this.hasBaseUrlTarget) data.baseUrl = this.baseUrlTarget.value
    if (this.hasTokenTarget) data.token = this.tokenTarget.value
    if (this.hasCompanyIdTarget) data.companyId = this.companyIdTarget.value
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }

  queryString() {
    const qs = []
    this.queryParamTargets.forEach((el) => {
      if (el.value === "") return
      qs.push(`${encodeURIComponent(el.dataset.paramName)}=${encodeURIComponent(el.value)}`)
    })
    return qs.length ? `?${qs.join("&")}` : ""
  }

  readSaved() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")
    } catch (_e) {
      return {}
    }
  }

  // Rebuilds the cURL example from the current field values and keeps the Copy
  // button's payload in sync. Token/Company-Id fall back to placeholders until
  // entered, so the snippet stays copy-pasteable.
  refreshCurl() {
    if (!this.hasCurlTarget) return

    const base = (this.baseUrlTarget?.value || window.location.origin).replace(/\/$/, "")
    const method = this.methodValue.toUpperCase()
    const token = (this.hasTokenTarget && this.tokenTarget.value) || "cck_yourtokenhere"

    const lines = [`curl -X ${method} '${base}${this.requestPath()}${this.queryString()}'`]
    lines.push(`-H 'Authorization: Bearer ${token}'`)
    if (this.requiresCompanyIdValue) {
      const companyId = (this.hasCompanyIdTarget && this.companyIdTarget.value) || "1006"
      lines.push(`-H 'Company-Id: ${companyId}'`)
    }
    const body = this.buildBody()
    if (body !== null && method !== "GET" && method !== "HEAD") {
      // When no body fields are filled in, keep the server-rendered skeleton
      // rather than overwriting it with an unhelpful "{}".
      const shownBody = body === "{}" && this.bodyTemplateValue ? this.bodyTemplateValue : body
      lines.push("-H 'Content-Type: application/json'")
      lines.push(`-d '${shownBody}'`)
    }

    const curl = lines.join(" \\\n  ")
    this.curlTarget.textContent = curl
    if (this.hasCopyButtonTarget) this.copyButtonTarget.dataset.copy = curl
  }

  // Path with placeholders substituted. When a path param is blank, the {name}
  // placeholder is kept (readable in the cURL); when sending, blanks just leave
  // the literal placeholder, which is fine since show/update need a value anyway.
  requestPath() {
    let path = this.pathValue
    this.pathParamTargets.forEach((el) => {
      const name = el.dataset.paramName
      if (el.value) path = path.replace(`{${name}}`, encodeURIComponent(el.value))
    })
    return path
  }

  async send(event) {
    event.preventDefault()
    this.persistShared()

    const startedAt = performance.now()
    this.responseWrapperTarget.classList.remove("hidden")
    this.responseStatusTarget.innerText = "Sending…"
    this.responseHeadersTarget.innerText = ""
    this.responseBodyTarget.innerText = ""

    try {
      const { url, options } = this.buildRequest()
      const response = await fetch(url, options)
      const elapsedMs = Math.round(performance.now() - startedAt)
      const headers = []
      response.headers.forEach((v, k) => headers.push(`${k}: ${v}`))
      const text = await response.text()
      let body = text
      try {
        body = JSON.stringify(JSON.parse(text), null, 2)
      } catch (_e) {
        // leave as raw text
      }

      this.responseStatusTarget.innerText = `${response.status} ${response.statusText} · ${elapsedMs} ms`
      this.responseStatusTarget.dataset.statusClass = response.ok ? "ok" : "err"
      this.responseHeadersTarget.innerText = headers.join("\n")
      this.responseBodyTarget.innerText = body
    } catch (e) {
      this.responseStatusTarget.innerText = "Request failed"
      this.responseStatusTarget.dataset.statusClass = "err"
      this.responseBodyTarget.innerText = String(e)
    }
  }
}
